import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { LanguageToggle } from '@/components/LanguageToggle';
import { GoogleAuthButton } from '@/components/GoogleAuthButton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { useI18n } from '@/context/LanguageContext';
import { errMsg } from '@/lib/api';
import { FREE_SIGNUP_CREDITS } from '@/lib/config';

export default function Register() {
  const { register } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      setError(t('auth.passwordTooShort'));
      return;
    }
    setBusy(true);
    setError('');
    try {
      await register(name.trim(), email.trim(), password);
      toast.success(t('auth.registerSuccess', { count: FREE_SIGNUP_CREDITS }));
      navigate('/studyo');
    } catch (err) {
      const m = errMsg(err, t('auth.registerFailed'));
      setError(m);
      toast.error(m);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center noise-bg px-4 py-10">
      <div className="absolute right-4 top-4 sm:right-6 sm:top-6">
        <LanguageToggle />
      </div>
      <div className="w-full max-w-[420px]">
        <Link to="/" className="mb-8 flex justify-center">
          <Logo />
        </Link>
        <div className="panel p-6 sm:p-8">
          <h1 className="font-display text-2xl font-semibold tracking-tight">{t('auth.registerTitle')}</h1>
          <p className="mt-2 text-sm text-[hsl(var(--text-muted))]">
            {t('auth.registerSubtitle', { count: FREE_SIGNUP_CREDITS })}
          </p>

          <div className="mt-7">
            <GoogleAuthButton testId="google-register-button" />
          </div>

          <div className="my-6 flex items-center gap-3">
            <span className="h-px flex-1 bg-[hsl(var(--border-soft))]" />
            <span className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--text-muted))]">
              {t('auth.or')}
            </span>
            <span className="h-px flex-1 bg-[hsl(var(--border-soft))]" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('auth.name')}</Label>
              <Input
                id="name"
                required
                minLength={2}
                data-testid="register-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('auth.namePlaceholder')}
                className="bg-[hsl(var(--surface-2))]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                required
                data-testid="register-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('auth.emailPlaceholder')}
                className="bg-[hsl(var(--surface-2))]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.password')}</Label>
              <Input
                id="password"
                type="password"
                required
                data-testid="register-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('auth.passwordMin')}
                className="bg-[hsl(var(--surface-2))]"
              />
            </div>

            {error && (
              <p data-testid="form-inline-error" className="rounded-[10px] border border-[hsl(var(--brand-red))] bg-[hsl(var(--surface-2))] px-3 py-2 text-sm text-[hsl(var(--text))]">
                {error}
              </p>
            )}

            <Button
              type="submit"
              disabled={busy}
              data-testid="register-submit-button"
              className="h-11 w-full bg-[hsl(var(--brand-red))] text-base font-semibold text-white hover:bg-[hsl(var(--brand-red-2))] active:scale-[0.99]"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t('auth.createAccount')}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-[hsl(var(--text-muted))]">
            {t('auth.haveAccount')}{' '}
            <Link to="/giris" data-testid="go-login-link" className="font-semibold text-[hsl(var(--brand-red))] hover:underline">
              {t('auth.loginLink')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
